import { useState, useEffect, useCallback } from 'preact/hooks';
import type { ProjectTag } from '@voice-assistant/shared';

const STORAGE_KEY = 'voice-assistant-checked-projects';

function loadChecked(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return new Set(JSON.parse(raw));
  } catch {}
  return new Set();
}

function saveChecked(set: Set<string>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch {}
}

export function useProjectTags() {
  const [tags, setTags] = useState<ProjectTag[]>([]);
  const [checkedProjects, setCheckedProjects] = useState<Set<string>>(loadChecked);

  useEffect(() => {
    fetch('/api/projects')
      .then((r) => r.json())
      .then((data: ProjectTag[]) => {
        setTags(data);
        // Prune stale localStorage keys that no longer exist in API
        const validKeys = new Set(data.map((t) => t.key));
        setCheckedProjects((prev) => {
          let changed = false;
          const next = new Set<string>();
          for (const k of prev) {
            if (validKeys.has(k)) {
              next.add(k);
            } else {
              changed = true;
            }
          }
          if (changed) {
            saveChecked(next);
            return next;
          }
          return prev;
        });
      })
      .catch(() => {});
  }, []);

  const toggleProject = useCallback((key: string) => {
    setCheckedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      saveChecked(next);
      return next;
    });
  }, []);

  const checkProject = useCallback((key: string) => {
    setCheckedProjects((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      saveChecked(next);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setCheckedProjects((prev) => {
      if (prev.size === 0) return prev;
      const empty = new Set<string>();
      saveChecked(empty);
      return empty;
    });
  }, []);

  const getCheckedKeys = useCallback(() => {
    return [...checkedProjects];
  }, [checkedProjects]);

  return { tags, checkedProjects, toggleProject, checkProject, clearAll, getCheckedKeys };
}
